import Papa from "papaparse";
import Encoding from "encoding-japanese";
import { CsvEncoding } from "./config.ts";

const UTF8_BOM = new Uint8Array([0xef, 0xbb, 0xbf]);

const buildCsvBlob = (bytes: Uint8Array, encoding: CsvEncoding): Blob => {
  const type =
    encoding === "utf8"
      ? "text/csv;charset=utf-8;"
      : "text/csv;charset=shift_jis;";
  return new Blob([bytes], { type });
};

const triggerDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", fileName + ".csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * CSV ファイルをダウンロードする関数
 */
export const downloadCSV = <T>(
  csvFormats: T[],
  fileName: string,
  encoding: CsvEncoding = "sjis",
) => {
  const bytes = createCSVFile<T>(csvFormats, undefined, encoding);
  triggerDownload(buildCsvBlob(bytes, encoding), fileName);
};

/**
 * 配列データをエンコード済み CSV のバイナリデータに変換する関数
 */
export const createCSVFile = <T>(
  csvFormats: T[],
  csvHeaders?: string[],
  encoding: CsvEncoding = "sjis",
) => {
  const csvContent = csvHeaders
    ? exportToCSVWithHeader(csvFormats, csvHeaders)
    : exportToCSV(csvFormats);
  return encodeCsv(csvContent, encoding);
};

export const exportToCSV = <T>(csvFormats: T[]): string => {
  if (csvFormats.length === 0) {
    return "";
  }

  return Papa.unparse(csvFormats, {
    quotes: true,
    skipEmptyLines: true,
  });
};

export const exportToCSVWithHeader = <T>(
  csvFormats: T[],
  csvHeaders: string[],
): string => {
  if (csvFormats.length === 0) {
    return "";
  }
  return Papa.unparse(
    {
      fields: csvHeaders,
      data: csvFormats,
    },
    {
      quotes: true,
      skipEmptyLines: true,
      header: true,
    },
  );
};

/**
 * CSV 文字列を指定エンコーディングのバイナリに変換する
 * UTF-8 は Excel 互換のため BOM を付与する
 */
export const encodeCsv = (
  csvContent: string,
  encoding: CsvEncoding,
): Uint8Array => {
  if (encoding === "utf8") {
    const utf8 = new TextEncoder().encode(csvContent);
    const withBom = new Uint8Array(UTF8_BOM.length + utf8.length);
    withBom.set(UTF8_BOM, 0);
    withBom.set(utf8, UTF8_BOM.length);
    return withBom;
  }
  return convertToSJIS(csvContent);
};

export const convertToSJIS = (csvContent: string) => {
  const unicodeArray = Encoding.stringToCode(csvContent);
  const sjisArray = Encoding.convert(unicodeArray, {
    to: "SJIS",
    from: "UNICODE",
    type: "array",
  });
  return new Uint8Array(sjisArray);
};

/**
 * CSV ファイルをダウンロードする関数（ヘッダー指定対応）
 */
export const downloadCSVWithHeaders = <T>(
  csvFormats: T[],
  csvHeaders: string[],
  fileName: string,
  encoding: CsvEncoding = "sjis",
) => {
  const bytes = createCSVFile(csvFormats, csvHeaders, encoding);
  triggerDownload(buildCsvBlob(bytes, encoding), fileName);
};

/**
 * ZIP ファイルをダウンロードする関数
 */
export const downloadZip = (zipFile: Uint8Array, fileName: string) => {
  const blob = new Blob([zipFile.buffer as ArrayBuffer], { type: "text/csv;charset=shift_jis;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `${fileName}.zip`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
